import { useMemo } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, ReferenceLine } from "recharts";
import { cn } from "@/lib/utils";

/**
 * Genera dati per una forma d'onda ECG realistica
 * Simula un ciclo PQRST tipico
 */
const generateECGData = (heartRate = 72, duration = 5, samplingRate = 200) => {
  const data = [];
  const totalSamples = duration * samplingRate;
  const cycleLength = (60 / heartRate) * samplingRate; // Samples per beat
  
  for (let i = 0; i < totalSamples; i++) {
    const t = (i % cycleLength) / samplingRate; // Time within cycle (seconds)
    const cyclePhase = (i % cycleLength) / cycleLength; // 0 to 1 within cycle
    
    let voltage = 0;
    
    // Baseline con leggero rumore
    const noise = (Math.random() - 0.5) * 0.02;
    
    // P wave (atrial depolarization) - occurs at ~10-20% of cycle
    if (cyclePhase >= 0.10 && cyclePhase < 0.20) {
      const pPhase = (cyclePhase - 0.10) / 0.10;
      voltage = 0.15 * Math.sin(pPhase * Math.PI);
    }
    
    // PR segment (flat) - 20-25% of cycle
    else if (cyclePhase >= 0.20 && cyclePhase < 0.25) {
      voltage = 0;
    }
    
    // QRS complex (ventricular depolarization) - 25-35% of cycle
    else if (cyclePhase >= 0.25 && cyclePhase < 0.35) {
      const qrsPhase = (cyclePhase - 0.25) / 0.10;
      
      if (qrsPhase < 0.2) {
        // Q wave (small negative)
        voltage = -0.1 * Math.sin(qrsPhase * 5 * Math.PI);
      } else if (qrsPhase < 0.5) {
        // R wave (large positive spike)
        const rPhase = (qrsPhase - 0.2) / 0.3;
        voltage = 1.2 * Math.sin(rPhase * Math.PI);
      } else if (qrsPhase < 0.7) {
        // S wave (negative dip)
        const sPhase = (qrsPhase - 0.5) / 0.2;
        voltage = -0.3 * Math.sin(sPhase * Math.PI);
      } else {
        // Return to baseline
        voltage = 0;
      }
    }
    
    // ST segment (flat, slightly elevated) - 35-50% of cycle
    else if (cyclePhase >= 0.35 && cyclePhase < 0.50) {
      voltage = 0.02; // Slight ST elevation (normal variant)
    }
    
    // T wave (ventricular repolarization) - 50-70% of cycle
    else if (cyclePhase >= 0.50 && cyclePhase < 0.70) {
      const tPhase = (cyclePhase - 0.50) / 0.20;
      voltage = 0.3 * Math.sin(tPhase * Math.PI);
    }
    
    // Baseline - rest of cycle
    else {
      voltage = 0;
    }
    
    data.push({
      time: i / samplingRate,
      voltage: voltage + noise,
      sample: i
    });
  }
  
  return data;
};

/**
 * Componente ECG Waveform
 * Visualizza una forma d'onda ECG animata/realistica
 */
export const ECGWaveform = ({ 
  heartRate = 72, 
  duration = 4,
  height = 200,
  showGrid = true,
  color = "hsl(var(--destructive))",
  className
}) => {
  const data = useMemo(() => generateECGData(heartRate, duration, 150), [heartRate, duration]);
  
  return (
    <div className={cn("w-full", className)}>
      <ResponsiveContainer width="100%" height={height}>
        <LineChart 
          data={data} 
          margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
        >
          {showGrid && (
            <CartesianGrid 
              strokeDasharray="1 1" 
              stroke="hsl(var(--border))" 
              strokeOpacity={0.5}
              horizontalPoints={[-0.5, 0, 0.5, 1]}
              verticalPoints={[0, 1, 2, 3, 4]}
            />
          )}
          <XAxis 
            dataKey="time" 
            type="number"
            domain={[0, duration]}
            tick={false}
            axisLine={{ stroke: 'hsl(var(--border))' }}
          />
          <YAxis 
            domain={[-0.5, 1.5]}
            tick={false}
            axisLine={{ stroke: 'hsl(var(--border))' }}
          />
          <ReferenceLine y={0} stroke="hsl(var(--muted-foreground))" strokeOpacity={0.3} />
          <Line 
            type="monotone" 
            dataKey="voltage" 
            stroke={color}
            strokeWidth={1.5}
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

/**
 * Componente ECG Monitor con stile monitor medico
 */
export const ECGMonitor = ({
  heartRate = 72,
  rhythm = "Sinusale",
  prInterval = 160,
  qrsDuration = 90,
  qtInterval = 400,
  className
}) => {
  return (
    <div className={cn("rounded-xl bg-gray-950 p-4 space-y-3", className)}>
      {/* Header con parametri vitali */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="text-green-400">
            <span className="text-3xl font-bold font-mono">{heartRate}</span>
            <span className="text-sm ml-1">bpm</span>
          </div>
          <div className="text-xs text-gray-400">
            <p>Ritmo: <span className="text-green-400">{rhythm}</span></p>
          </div>
        </div>
        <div className="flex items-center gap-3 text-xs font-mono">
          <div className="text-gray-400">
            PR: <span className="text-white">{prInterval}ms</span>
          </div>
          <div className="text-gray-400">
            QRS: <span className="text-white">{qrsDuration}ms</span>
          </div>
          <div className="text-gray-400">
            QT: <span className="text-white">{qtInterval}ms</span>
          </div>
        </div>
      </div>
      
      {/* Waveform */}
      <div className="bg-gray-900 rounded-lg p-2 border border-gray-800">
        <ECGWaveform 
          heartRate={heartRate}
          duration={4}
          height={150}
          color="#22c55e"
          showGrid={true}
        />
      </div>
      
      {/* Grid scale indicator */}
      <div className="flex items-center justify-between text-xs text-gray-500">
        <span>25mm/s</span>
        <span>10mm/mV</span>
        <span>Lead II</span>
      </div>
    </div>
  );
};

export default ECGWaveform;
